<template>
  <form class="form">
    <svg class="logo">
      <use xlink:href="#bookero-logo" />
    </svg>

    <div class="wrapper">
      <h1>{{ $t('register.title') }}</h1>

      <hr>

      <transition-group name="slide-down">
        <message
          v-if="this.errors.length && this.errors.join('').indexOf('is-wrong') === -1"
          type="error"
          key="msg-1">
          {{ $t('register.allFieldsAreRequired') }}
        </message>

        <message
          v-if="this.errors.join('').indexOf('is-wrong') > -1"
          type="error"
          key="msg-2">
          {{ $t('register.someFieldsAreInvalid') }}
        </message>

        <message
          v-if="this.errors.length === 1 && this.errors.indexOf('terms') > -1"
          type="error"
          key="msg-4">
          {{ $t('register.acceptingTosIsRequired') }}
        </message>

        <message
          v-if="connectionError"
          type="error"
          key="msg-5">
          {{ $t('register.anErrorOccurred') }}
        </message>

        <message
          v-if="validationError"
          type="error"
          key="msg-6">
          <span v-pure-html="validationError"></span>
        </message>
      </transition-group>

      <label>
        {{ $t('register.companyName') }}
        <input
          type="text"
          v-model="name"
          @keyup="clearError('name')"
          tabindex="1"
          :class="{ 'is-invalid': this.errors.indexOf('name') > -1 }"
          :placeholder="$t('register.yourCompanyName')" />
      </label>

      <label>
        {{ $t('register.email') }}
        <input
          type="email"
          v-model="email"
          @keyup="clearErrors('email,email-is-wrong')"
          tabindex="1"
          :class="{ 'is-invalid': this.errors.indexOf('email') > -1 || this.errors.indexOf('email-is-wrong') > -1 }"
          :placeholder="$t('register.yourEmail')" />
      </label>

      <div class="passwords">
        <label>
          {{ $t('register.password') }}

          <input
            :type="passwordIsHidden ? 'password' : 'text'"
            v-model="password"
            @keyup="clearErrors('password,password-is-wrong')"
            tabindex="2"
            spellcheck="false"
            :class="{ 'is-invalid': this.errors.indexOf('password') > -1 || this.errors.indexOf('password-is-wrong') > -1 }"
            :placeholder="$t('register.yourPassword')" />

          <span
            v-if="password"
            class="password-toggle"
            @click="passwordIsHidden = !passwordIsHidden">
            <template v-if="passwordIsHidden">{{ $t('common.ui.show') }}</template>
            <template v-else>{{ $t('common.ui.hide') }}</template>
          </span>
        </label>

        <small
          :class="{ 'note': true, 'is-invalid': this.errors.indexOf('password-is-wrong') > -1 }">
          {{ $t('register.passwordMustHave6Chars') }}
        </small>
      </div>

      <label class="terms">
        <switcher
          key="terms-switcher"
          v-model="terms" />
        <span
          :class="{ 'is-invalid': this.errors.indexOf('terms') > -1 }"
          v-pure-html="$t('register.tosText', { tosUrl: $config.uiLanguage === 'pl' ? 'https://www.bookero.pl/regulamin' : 'https://www.bookero.org/terms-and-conditions', ppUrl: $config.uiLanguage === 'pl' ? 'https://www.bookero.pl/polityka-prywatnosci' : 'https://www.bookero.org/privacy-policy' })"></span>
      </label>

      <div
        class="disclaimer"
        v-pure-html="$t('register.gdprText')">
      </div>

      <btn
        type="success wide"
        @click.native="register"
        :disabled="formSent"
        :tabindex="3">
        <template v-if="!formSent">{{ $t('register.register') }}</template>
        <template v-if="formSent">{{ $t('register.sending') }}</template>
      </btn>

      <div
        class="footer">
        <span>{{ $t('register.doYouHaveAnAccount') }}</span>
        <router-link
          to="/login"
          tabindex="4">
          {{ $t('register.login') }}
        </router-link>
      </div>
    </div>

    <btn
      v-if="$root.appLanguage === 'pl'"
      type="with-icon icon-larger"
      title="Switch to english"
      iconName="en"
      iconColor="color-text-light"
      @click.native.prevent="setLang('en')"
      class="switch-lang">
      Switch to english
    </btn>

    <btn
      v-if="$root.appLanguage === 'en'"
      type="with-icon icon-larger"
      title="Zmień na polski"
      iconName="pl"
      iconColor="color-text-light"
      @click.native.prevent="setLang('pl')"
      class="switch-lang">
      Zmień na polski
    </btn>
  </form>
</template>

<script>
import Vue from 'vue';
import FormHelpers from './../mixins/FormHelpers';
import SanitizeUtils from './../helpers/SanitizeUtils';
import SessionUtils from './../helpers/SessionUtils';

export default {
  name: 'register',
  mixins: [
    FormHelpers
  ],
  data () {
    return {
      email: '',
      name: '',
      password: '',
      passwordIsHidden: true,
      terms: false,
      formSent: false,
      errors: [],
      connectionError: false,
      validationError: ''
    };
  },
  computed: {
    packageID () {
      if (this.$route.params.package_id) {
        let id = parseInt(this.$route.params.package_id, 10);

        if (id >= 1 && id <= 3) {
          return id;
        }

        return 3;
      }

      return 3;
    }
  },
  watch: {
    'terms': function (newValue) {
      if (newValue) {
        this.clearError('terms');
      }
    }
  },
  mounted () {
    localStorage.removeItem('bookero-auth-token');
    Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer null';
    this.$store.commit('config/clearConfig');
    this.$store.commit('global/clear');

    if (typeof window.bugsnagClient !== 'undefined') {
      window.bugsnagClient.setUser('0');
    }

    if (typeof window.ga !== 'undefined') {
      window.ga('set', 'dimension1', 0);
    }
  },
  methods: {
    register (e) {
      e.preventDefault();
      this.connectionError = false;

      if (!this.validate()) {
        return;
      }

      this.formSent = true;

      let queryParams = this.$route.query;
      let registerData = {
        'name': this.name,
        'email': this.email,
        'password': this.password,
        'terms': this.terms,
        'package_id': this.packageID
      };

      if (queryParams.pt && typeof queryParams.pt === 'string') {
        registerData.pt = queryParams.pt;
      }

      this.$http.post('/register', registerData).then(response => {
        localStorage.setItem('bookero-auth-token', response.data.token);
        localStorage.setItem('bookero-last-visit-time', new Date().getTime());
        SessionUtils.storeLoginTime();
        Vue.prototype.$http.defaults.headers['Authorization'] = 'Bearer ' + SanitizeUtils.getToken();
        this.formSent = false;

        window.bookeroFreshLogin = true;

        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'registration'
          });
        }

        if (typeof window.oaiq === 'function') {
          window.oaiq(
            'measure',
            'registration_completed',
            { type: 'customer_action' }
          );
        }

        setTimeout(() => {
          this.$router.push('/dashboard/welcome');

          setTimeout(() => {
            this.$bus.$emit('login-success');
          }, 1000);
        }, 750);
      }).catch(error => {
        if (error.response && error.response.status === 422) {
          this.validationError = [];
          let errorKeys = Object.keys(error.response.data.errors);

          for (let i = 0; i < errorKeys.length; i++) {
            this.errors.push(errorKeys[i] + '-is-wrong');
            this.validationError.push(error.response.data.errors[errorKeys[i]].join('<br>'));
          }

          this.validationError = this.validationError.join('<br>');
        }

        if (!error.response) {
          this.connectionError = true;

          setTimeout(() => {
            this.connectionError = false;
          }, 3000);
        }

        this.formSent = false;
      });
    },
    validate () {
      this.errors = [];
      this.validationError = '';

      if (this.name.trim() === '') {
        this.errors.push('name');
      }

      if (this.email.trim() === '') {
        this.errors.push('email');
      } else if (this.email.indexOf('@') === -1 || this.email.length < 5) {
        this.errors.push('email-is-wrong');
      }

      if (this.password.trim() === '') {
        this.errors.push('password');
      } else if (this.password.length < 6) {
        this.errors.push('password-is-wrong');
      }

      if (!this.terms) {
        this.errors.push('terms');
      }

      return this.errors.length === 0;
    },
    setLang (newLang) {
      this.$bus.$emit('bookero-set-language', newLang);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.form {
  left: 50%;
  margin: 0;
  max-width: 94%;
  padding: 3.2rem 0;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  transition: opacity .5s ease-out;
  width: 46rem;
  will-change: opacity;

  .message {
    max-width: 100%;
    width: 100%;
  }

  a {
    &:active,
    &:focus,
    &:hover {
      color: $color-primary;
    }
  }

  &.is-hidden {
    opacity: 0;
    pointer-events: none;
  }

  .logo {
    display: block;
    height: 3.6rem;
    margin: 0 auto 3.2rem auto;
  }

  .wrapper {
    background: $color-bg-lightest;
    border-radius: $border-radius-small;
    padding: 3rem;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      margin: 0 0 3rem 0;
      text-align: center;
    }

    hr {
      border: none;
      border-top: 1px solid $color-border;
      margin-bottom: 3rem;
    }

    label {
      display: block;
      font-weight: 400;
      position: relative;

      .is-optional {
        color: $color-text-light;
        display: inline-block;
        float: right;
        margin-top: 2px;
        text-align: right;
        width: 120px;
      }

      input {
        background: $color-bg-lightest;
        border: 1px solid $color-border;
        border-radius: $border-radius-small;
        font-size: 1.4rem;
        margin: .5rem 0 1rem 0;
        padding: 1rem;
        width: 100%;

        &::-webkit-input-placeholder {
          color: $color-text-lightest;
        }

        &::-moz-placeholder {
          color: $color-text-lightest;
        }

        &:-ms-input-placeholder {
          color: $color-text-lightest;
        }

        &:-moz-placeholder {
          color: $color-text-lightest;
        }

        &.is-invalid {
          border-color: $color-danger;
          color: $color-danger;
        }
      }
    }

    & > .button {
      margin: 1.5rem auto;
    }
  }

  .passwords {
    position: relative;
    .note {
      color: $color-text-light;
      margin-top: -.5rem;
      width: 100%;

      &.is-invalid {
        color: $color-danger;
      }
    }

    .password-toggle {
      background: $color-notice;
      border-radius: 5px;
      color: $color-light;
      cursor: pointer;
      font-size: 13px;
      padding: 3px 6px;
      position: absolute;
      right: 1rem;
      top: calc(50% - 4px);
    }
  }

  .terms {
    display: flex!important;
    margin: 2rem 0;

    .switcher {
      margin-right: 2rem;
      min-width: 4rem;
    }

    span {
      font-size: 1.4rem;

      &.is-invalid {
        color: $color-danger;
      }
    }
  }

  .disclaimer {
    color: $color-text-light;
    font-size: 1.2rem;
    margin-bottom: 2.5rem;
  }

  .footer {
    background: $color-bg-medium;
    border-radius: 0 0 $border-radius-small $border-radius-small;
    margin: 3rem -3rem -3rem -3rem;
    padding: 2rem 3rem;
    text-align: center;

    a,
    span {
      display: block;
      line-height: 1.5;
    }
  }
}

.slide-down-enter-active, .slide-down-leave-active {
  transition: all .25s ease-out;
}

.slide-down-enter, .slide-down-leave-to {
  height: 0;
  opacity: 0;
}

@media (max-width: 480px) {
  .form {
    .wrapper {
      padding: 2rem;
    }

    .footer {
      margin: 3rem -2rem -2rem -2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/Register.vue